import { useTranslation } from "react-i18next";
import { useLocation, useMatches, useParams } from "react-router-dom";
import { useFetchDetail } from "@hooks/useFetchDetail";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import BackgroundImageCst from "@components/BackgroundImageCst";
import Breadcrumb from "@components/Breadcrumb";
import React, { useEffect, useState } from "react";
import AnimationFadeIn from "@components/AnimationFadeIn";
import HeroCst from "@components/HeroCst";
import {
  Box,
  Container,
  Grid,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import Navigation from "@pages/Contents/Corporate/Notifications/components/navigation/Navigation";
import { useNotificationState } from "@pages/Contents/Corporate/Notifications/context/NotificationContext";
import { convertDateTimeToDMYHM } from "@pages/Contents/Corporate/Notifications/utils/utils";
import { ArrowCircleLeft } from "@mui/icons-material";

const EditNotification: React.FC = () => {
  const { t } = useTranslation(["notification-page"]);
  const { t: inputTranslations } = useTranslation(["input"]);
  const { id, idNotification } = useParams();
  const { state, dispatch } = useNotificationState();
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
    description?: string;
  }>();
  const matches = useMatches();
  const location = useLocation();
  const { t: commonTranslations } = useTranslation(["common"]);
  const findPathIndex = matches.findIndex(
    (el) => el.pathname === location.pathname,
  );

  const { columns, dataDetail } = useFetchDetail(
    `${ServicesURL.getNotification.replace("idCorporate", id ? id : "1")}`,
    `${idNotification}`,
    PagesID["corporate.notifications.add"],
  );

  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.down("xl"));

  const steps = !state?.catalogueFilterEnabled
    ? [t("general"), t("template"), t("users"), t("schedule")]
    : [t("general"), t("template"), t("lo"), t("users"), t("schedule")];

  //TODO: REFACTOR
  const dispatchNestedObject = (obj: any, parentKey: string | null = null) => {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        switch (key) {
          case "event": {
            const enrolled_days_ago = obj[key].parameters.find(
              (item: { name: string }) => item.name === "ENROLLED_DAYS_AGO",
            );

            const max_lo_number = obj[key].parameters.find(
              (item: { name: string }) => item.name === "max_lo_number",
            );

            const expired_days_ago = obj[key].parameters.find(
              (item: { name: string }) => item.name === "expired_days_ago",
            );

            const published_days_ago = obj[key].parameters.find(
              (item: { name: string }) => item.name === "published_days_ago",
            );

            if (enrolled_days_ago) {
              dispatch({
                type: "SET_INPUT_VALUES",
                payload: {
                  key: "flagEnrolledDaysAgo",
                  value: !!enrolled_days_ago?.value ?? false,
                },
              });
            }

            if (max_lo_number) {
              dispatch({
                type: "SET_INPUT_VALUES",
                payload: {
                  key: "flagMaxLoNumber",
                  value: !!max_lo_number?.value ?? false,
                },
              });
            }
            if (expired_days_ago) {
              dispatch({
                type: "SET_INPUT_VALUES",
                payload: {
                  key: "flagExpiredDaysAgo",
                  value: !!expired_days_ago?.value ?? false,
                },
              });
            }

            if (published_days_ago) {
              dispatch({
                type: "SET_INPUT_VALUES",
                payload: {
                  key: "flagPublishedDaysAgo",
                  value: !!published_days_ago?.value ?? false,
                },
              });
            }

            dispatch({
              type: "SET_INPUT_VALUES",
              payload: {
                key: key,
                value: obj[key].name,
              },
            });
            dispatch({
              type: "SET_INPUT_VALUES",
              payload: {
                key: "scheduleOptionsTypes",
                value: obj[key].scheduleTypes,
              },
            });
            dispatch({
              type: "SET_INPUT_VALUES",
              payload: {
                key: "scheduleOptionsUnits",
                value: obj[key].scheduleUnits,
              },
            });
            break;
          }

          case "templates": {
            const rawEmailBody: string = obj[key].emailBody || "";
            // If stored HTML lacks DOCTYPE (legacy body-only saves), wrap in a complete email document
            const isFullHtml = rawEmailBody
              .trimStart()
              .toLowerCase()
              .startsWith("<!doctype");
            const emailDescValue =
              rawEmailBody && !isFullHtml
                ? `<!DOCTYPE html>
<html lang="it" xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
  <meta charset="UTF-8">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title></title>
  <style type="text/css">
    body { margin: 0; padding: 0; -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%; }
    table, td { border-collapse: collapse; mso-table-lspace: 0pt; mso-table-rspace: 0pt; }
    img { border: 0; height: auto; line-height: 100%; outline: none; text-decoration: none; -ms-interpolation-mode: bicubic; }
    p { display: block; margin: 13px 0; }
  </style>
</head>
<body>
${rawEmailBody}
</body>
</html>`
                : rawEmailBody;
            dispatch({
              type: "SET_INPUT_VALUES",
              payload: {
                key: "email_desc",
                value: emailDescValue,
              },
            });
            if (isFullHtml) {
              dispatch({
                type: "SET_INPUT_VALUES",
                payload: { key: "email_desc_mail_editor", value: rawEmailBody },
              });
            }
            dispatch({
              type: "SET_INPUT_VALUES",
              payload: {
                key: "email_subject",
                value: obj[key].emailSubject,
              },
            });

            dispatch({
              type: "SET_INPUT_VALUES",
              payload: {
                key: "email_cc",
                value: obj[key].emailCc,
              },
            });

            dispatch({
              type: "SET_INPUT_VALUES",
              payload: {
                key: "applicative_subject",
                value: obj[key].applicativeSubject,
              },
            });

            dispatch({
              type: "SET_INPUT_VALUES",
              payload: {
                key: "applicative_desc",
                value: obj[key].applicativeBody,
              },
            });

            dispatch({
              type: "SET_INPUT_VALUES",
              payload: {
                key: "push_mobile_subject",
                value: obj[key].pushSubject,
              },
            });

            dispatch({
              type: "SET_INPUT_VALUES",
              payload: {
                key: "push_mobile_desc",
                value: obj[key].pushBody,
              },
            });

            dispatch({
              type: "SET_INPUT_VALUES",
              payload: {
                key: "email_sender",
                value: obj[key].emailSender,
              },
            });

            dispatch({
              type: "SET_INPUT_VALUES",
              payload: {
                key: "email_display_name",
                value: obj[key].emailDisplayName,
              },
            });

            break;
          }
        }

        const newKey = parentKey ? `${parentKey}.${key}` : key;

        if (
          typeof value === "object" &&
          value !== null &&
          !Array.isArray(value)
        ) {
          dispatchNestedObject(value, newKey);
        } else {
          switch (newKey) {
            case "schedule.dayOfWeek": {
              if (obj[key] && obj[key].length > 0) {
                let values = [];
                for (let i = 0; i < obj[key].length; i++) {
                  values.push({
                    label: inputTranslations(
                      `options.days_of_week.${obj[key][i]}`,
                    ),
                    value: `${obj[key][i]}`,
                  });
                }
                dispatch({
                  type: "SET_INPUT_VALUES",
                  payload: {
                    key: "dayOfWeek",
                    value: values,
                  },
                });
              }
              break;
            }
            case "schedule.dayOfMonth": {
              if (obj[key] && obj[key].length > 0) {
                let values = [];
                for (let i = 0; i < obj[key].length; i++) {
                  values.push({
                    label: inputTranslations(obj[key][i]),
                    value: `${obj[key][i]}`,
                  });
                }
                dispatch({
                  type: "SET_INPUT_VALUES",
                  payload: {
                    key: "dayOfMonth",
                    value: values,
                  },
                });
              }
              break;
            }
            case "schedule.hourOfDay": {
              if (obj[key] && obj[key].length > 0) {
                let values;
                if (obj.everyUnit === "DAY") {
                  //IF EVERY DAY HANDLE HOURS AS ARRAY
                  let values = [];
                  for (let i = 0; i < obj[key].length; i++) {
                    values.push({
                      label: `${obj[key][i]}:00`,
                      value: `${obj[key][i]}`,
                    });
                  }
                } else {
                  //IF EVERY WEEK/MONTH HANDLE HOUR
                  values = `${obj[key]}`;
                }
                dispatch({
                  type: "SET_INPUT_VALUES",
                  payload: {
                    key: "hourOfDay",
                    value: values,
                  },
                });
              }
              break;
            }
            case "schedule.specificDate": {
              const date = convertDateTimeToDMYHM(obj[key]);
              const newDate = new Date(date);
              dispatch({
                type: "SET_INPUT_VALUES",
                payload: {
                  key: key,
                  value: newDate.toISOString(),
                },
              });
              break;
            }
            default: {
              dispatch({
                type: "SET_INPUT_VALUES",
                payload: {
                  key: key,
                  value: obj[key],
                },
              });
            }
          }
        }
      }
    }
  };

  function handleDataDetailChange(dataDetail: any) {
    if (!idNotification && !dataDetail) return;
    dispatch({
      type: "RESET_INPUT_VALUES",
    });
    dispatchNestedObject(dataDetail);
  }

  // if (dataDetail && Object.keys(state).length === 0) {
  //   handleDataDetailChange(dataDetail);
  // }

  useEffect(() => {
    if (dataDetail) handleDataDetailChange(dataDetail);
  }, [dataDetail]);

  const handleRedirect = () => {
    const redirectPath = matches[findPathIndex - 1]?.pathname;

    if (redirectPath) {
      window.location.assign(redirectPath);
    }
  };

  return (
    <AnimationFadeIn>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-survey.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={t("edit.hero-title")}
          description={t("edit.hero-description")}
        />
        <Box
          sx={{
            mt: "25px",
          }}
        >
          <Link
            component="button"
            sx={{
              color: "white",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              mb: "24px",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
            onClick={handleRedirect}
          >
            <ArrowCircleLeft sx={{ fontSize: "32px" }} />
            <Typography sx={{ fontWeight: "600" }}>
              {commonTranslations("backButton")}
            </Typography>
          </Link>
          <Grid container>
            {!isLargeScreen && (
              <Grid item lg={0} xl={2}>
                <BackgroundImageCst
                  image="section-internal-form.jpg"
                  disableGradient
                />
              </Grid>
            )}
            <Grid
              item
              lg={12}
              xl={10}
              sx={{
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  padding: "32px",
                  minHeight: "495px",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Navigation steps={steps} columns={columns} />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>
      <ModalConfirmsCst
        open={!!openToast}
        title={t("hero-title-edit")}
        description={""}
        onCallBackClosed={() => {
          setOpenToast(undefined);
        }}
        status={openToast}
      />
    </AnimationFadeIn>
  );
};
export default EditNotification;
